import { useState } from "react";
import { api } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Calendar,
  User,
  MapPin,
  MoreVertical,
  Trash2,
  Edit2,
  ChevronRight,
  AlertTriangle,
  Receipt,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar as CalendarComponent } from "./ui/calendar";
import { format, parseISO, differenceInDays } from "date-fns";

export const OrderCard = ({ order, statuses, sources, onStatusChange, onDelete, onUpdate, canEdit }) => {
  // PrintHQ owns this record; we mirror it. Its API accepts status and notes only, and the
  // backend rejects anything else outright, so the edit dialog must not offer the other fields.
  const isMirrored = order.origin === "printhq";

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editData, setEditData] = useState({
    customer_name: order.customer_name,
    source: order.source,
    due_date: order.due_date,
    status: order.status,
    notes: order.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return format(parseISO(dateStr), "MMM d, yyyy");
    } catch {
      return dateStr;
    }
  };

  // PrintHQ raises most orders straight off a paid invoice, with no due date at all.
  const getDaysUntilDue = () => {
    if (!order.due_date) return null;
    try {
      const dueDate = parseISO(order.due_date);
      const days = differenceInDays(dueDate, new Date());
      return Number.isNaN(days) ? null : days;
    } catch {
      return null;
    }
  };

  const daysUntilDue = getDaysUntilDue();
  const isOverdue = daysUntilDue !== null && daysUntilDue < 0;
  const isDueSoon = daysUntilDue !== null && daysUntilDue >= 0 && daysUntilDue <= 3;

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const payload = isMirrored
        ? { status: editData.status, notes: editData.notes }
        : editData;
      await api.put(`/orders/${order.id}`, payload);
      toast.success("Order updated");
      setEditDialogOpen(false);
      onUpdate();
    } catch (error) {
      toast.error(error.message || "Failed to update order");
    } finally {
      setSaving(false);
    }
  };

  const handleDateSelect = (date) => {
    if (date) {
      setEditData({ ...editData, due_date: format(date, "yyyy-MM-dd") });
    }
  };

  return (
    <>
      <div className="win-raised p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <User className="w-4 h-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
            <span className="font-medium text-foreground truncate">
              {order.customer_name}
            </span>
            {isMirrored && (
              <span
                className="win-well px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground flex-shrink-0"
                title="Owned by PrintHQ - status and notes can be changed here"
              >
                {order.order_number || "PrintHQ"}
              </span>
            )}
          </div>
          {canEdit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="cursor-pointer">
                    <ChevronRight className="w-4 h-4 mr-2" />
                    Move to Status
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {statuses.map((status) => (
                      <DropdownMenuItem
                        key={status.id}
                        onClick={() => onStatusChange(order.id, status.name)}
                        className="cursor-pointer"
                      >
                        {status.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuItem onClick={() => setEditDialogOpen(true)} className="cursor-pointer">
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit Order
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setDeleteDialogOpen(true)}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Order
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Info */}
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span>{order.source}</span>
          </div>
          {order.due_date && (
            <div className="flex items-center gap-2">
              {isOverdue ? (
                <AlertTriangle className="w-3.5 h-3.5 text-destructive" strokeWidth={1.5} />
              ) : (
                <Calendar className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
              )}
              <span className={isOverdue ? "text-destructive" : isDueSoon ? "text-amber-500" : "text-muted-foreground"}>
                {formatDate(order.due_date)}
                {daysUntilDue !== null && (
                  <span className="ml-1">
                    {isOverdue
                      ? `(${Math.abs(daysUntilDue)}d overdue)`
                      : daysUntilDue === 0
                        ? "(today)"
                        : `(${daysUntilDue}d)`}
                  </span>
                )}
              </span>
            </div>
          )}
          {order.invoice_id && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Receipt className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span className="text-xs">Linked to invoice</span>
            </div>
          )}
        </div>

        {/* Notes Preview */}
        {order.notes && (
          <p className="text-xs text-muted-foreground mt-3 line-clamp-2">{order.notes}</p>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {isMirrored && (
              <p className="text-sm text-muted-foreground">
                This order lives in PrintHQ ({order.order_number || "no number"}). Only its status
                and notes can be changed from here — edit the rest in PrintHQ.
              </p>
            )}

            {!isMirrored && (
              <>
                <div className="space-y-2">
                  <Label>Customer Name</Label>
                  <Input
                    value={editData.customer_name}
                    onChange={(e) => setEditData({ ...editData, customer_name: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Source</Label>
                  <Select value={editData.source} onValueChange={(value) => setEditData({ ...editData, source: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {sources.map((source) => (
                        <SelectItem key={source.id} value={source.name}>{source.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <Calendar className="mr-2 h-4 w-4" />
                        {editData.due_date ? formatDate(editData.due_date) : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={editData.due_date ? parseISO(editData.due_date) : undefined}
                        onSelect={handleDateSelect}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={editData.status} onValueChange={(value) => setEditData({ ...editData, status: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {statuses.map((status) => (
                    <SelectItem key={status.id} value={status.name}>{status.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={editData.notes}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                className="min-h-[80px]"
                placeholder="Order notes..."
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSaveEdit} disabled={saving} className="flex-1">
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Order</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the order for &quot;{order.customer_name}&quot;?{" "}
              {isMirrored
                ? "This order is owned by PrintHQ, so it will be deleted there too. This action cannot be undone."
                : "This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => onDelete(order.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
